// Arma el texto que se manda por WhatsApp: nombre, descripción,
// características y link. Es el pie de foto del flyer cuando el dispositivo
// soporta compartir archivos, y el mensaje completo cuando no.
//
// Las características viven en campos distintos según el tipo (los brazos
// tienen priceSlice/priceFull, las galletas priceUnit y packs, la panadería
// options[]), así que hay una rama por tipo.

import { SITE_URL } from './structuredData';

// WhatsApp corta el pie de foto alrededor de los 1024 caracteres. Dejamos
// margen: lo que se recorta es la descripción, nunca los precios ni el link.
const MAX_LEN = 1000;

const isPrice = (n) => typeof n === 'number' && !Number.isNaN(n) && n > 0;
const money = (n) => `$${Number(n).toFixed(2)}`;

// Con barra final, como el resto de rutas del sitio: nginx sirve el directorio
// y así el link compartido no se come un 301 antes de abrir.
export const productPath = (slug) => `/producto/${slug}/`;
export const productUrl = (slug) => `${SITE_URL}${productPath(slug)}`;

// Las fotos llegan del API como absolutas contra www.brazolovers.com, que hace
// 301 al apex. Eso cuesta caro en los dos sitios donde las usamos:
//   - og:image: los scrapers sociales no siempre siguen el redirect → sin foto
//     en la tarjeta de WhatsApp.
//   - el flyer: www es OTRO origen, así que el canvas la trataría como externa.
// Aquí las dejamos siempre en el apex, en forma relativa.
export const productImagePath = (src) => {
  if (!src) return '/placeholder.jpg';
  const match = String(src).match(/^https?:\/\/(?:www\.)?brazolovers\.com(\/.*)$/i);
  return match ? match[1] : src;
};

// La misma foto en absoluto, siempre sobre el apex.
export const productImageUrl = (src) => {
  const path = productImagePath(src);
  return /^https?:\/\//.test(path) ? path : `${SITE_URL}${path}`;
};

// La foto para og:image y el JSON-LD, es decir: la que leen los scrapers.
//
// El catálogo es .webp, y el soporte de webp en la tarjeta de preview de
// WhatsApp no es fiable — el link compartido puede acabar sin foto. Por eso
// existe un JPEG hermano en /images/og/ (lo genera scripts/make-og-jpg.js) y es
// el que se anuncia. La página sigue pintando el .webp, que pesa menos.
export const productOgImageUrl = (src) => {
  const path = productImagePath(src);
  const jpg = path.replace(/^\/images\/([^/]+)\.webp$/i, '/images/og/$1.jpg');
  return /^https?:\/\//.test(jpg) ? jpg : `${SITE_URL}${jpg}`;
};

// La descripción del modal mete un "Tip de Oro" después del 🔥, con formato
// propio en la UI. No es parte de la descripción, así que fuera.
const cleanDescription = (raw) =>
  String(raw || '')
    .split('🔥')[0]
    .replace(/\s+/g, ' ')
    .trim();

// Recorta por palabra, sin dejar un guión o una coma colgando al final.
const truncate = (text, max) => {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 40 ? lastSpace : cut.length).replace(/[\s,;:.-]+$/, '')}…`;
};

// Las líneas de características, una por tipo de producto.
//
// SIN emoji a propósito. Los que marcaban cada línea (💵 📏 🏷️ …) viven fuera
// del plano básico de Unicode y llegaban rotos al chat en algunos teléfonos:
// el mensaje se veía con un rombo de interrogación en cada línea. Una etiqueta
// en palabras no se puede romper, y se lee igual de bien.
const featureLines = (product, type) => {
  const lines = [];

  if (type === 'brazo') {
    const prices = [];
    if (isPrice(product.priceSlice) && product.availableSlice !== false) {
      prices.push(`Porción ${money(product.priceSlice)}`);
    }
    if (isPrice(product.priceFull) && product.availableFull !== false) {
      prices.push(`Brazo completo ${money(product.priceFull)}`);
    }
    if (prices.length) lines.push(`Precio: ${prices.join(' · ')}`);
    lines.push('Tamaño: 30 cm · rinde 10-12 porciones');
    if (product.category) lines.push(`Categoría: ${product.category}`);
    return lines;
  }

  if (type === 'galleta') {
    if (isPrice(product.priceUnit)) lines.push(`Precio: ${money(product.priceUnit)} la unidad`);
    const packs = [];
    if (isPrice(product.pricePack5)) packs.push(`5 por ${money(product.pricePack5)}`);
    if (isPrice(product.pricePack12)) packs.push(`12 por ${money(product.pricePack12)}`);
    if (packs.length) lines.push(`Packs: ${packs.join(' · ')}`);
    return lines;
  }

  if (type === 'pastel') {
    const sizes = (product.sizes || []).filter((s) => isPrice(s.price) && s.available !== false);
    if (sizes.length) {
      for (const size of sizes) {
        const portions = size.portions ? ` (${size.portions})` : '';
        lines.push(`${size.name}${portions} — ${money(size.price)}`);
      }
    } else if (isPrice(product.price)) {
      lines.push(`Precio: ${money(product.price)}`);
    }
    if (product.portions) lines.push(`Rinde: ${product.portions}`);
    if (product.isConfigurable) lines.push('Se arma a tu gusto: tamaño, sabor y relleno');
    return lines;
  }

  // Panadería, desayunos, menú salado y bebidas: el precio real está en las
  // variantes cuando las hay.
  const options = product.hasOption && Array.isArray(product.options) ? product.options : [];
  if (options.length) {
    for (const option of options) {
      const note = option.note ? ` — ${option.note}` : '';
      lines.push(`• ${option.name}: ${money(option.price)}${note}`);
    }
  } else if (isPrice(product.price)) {
    lines.push(`Precio: ${money(product.price)}`);
  }
  return lines;
};

// El mensaje completo. `slug` viene de productSlug/buildProductIndex.
export const buildShareMessage = (product, type, slug) => {
  if (!product) return '';

  const header = `*${product.name}* · Brazolovers`;

  const features = featureLines(product, type);
  if (product.bajoPedido) features.push('Bajo pedido — pídelo con 24 horas de anticipación');
  if (product.available === false) features.push('Agotado por el momento');

  const footer = productUrl(slug);

  // Todo menos la descripción es intocable; el presupuesto que sobra es suyo.
  const fixed = [header, features.join('\n'), footer].filter(Boolean).join('\n\n');
  const budget = MAX_LEN - fixed.length - 2; // los dos saltos que la separan

  const description = cleanDescription(product.description);
  const body = description && budget > 60 ? truncate(description, budget) : '';

  return [header, body, features.join('\n'), footer].filter(Boolean).join('\n\n');
};

export { cleanDescription, featureLines };
